<template>
  <focus-trap :clickDeactivates="false" :escapeDeactivates="false">
    <div
      class="print-measurement-results-popup"
      :tabindex="`-${layerNumber}`"
      @keyup.esc.stop="handleClose"
    >
      <popup
        :buttons="buttons"
        :content-style="{
          maxHeight: '650px',
          marginTop: '25px',
          padding: '0 30px'
        }"
        :disabled-flg="!canPrint"
        :layer-number="layerNumber"
        @cancel="handleClose"
        @click-close-mark="handleClose"
        @decision="openPdf"
      >
        <template v-slot:content>
          <div class="content">
            <div class="title">検査結果の印刷</div>
            <div v-if="alertText" class="alert" data-test="alert">
              <div class="description">{{ alertDescription }}</div>
              <div class="text">{{ alertText }}</div>
            </div>
            <div class="table" data-test="table" :style="styles.table">
              <div
                class="table-header"
                data-test="table-header"
                :style="styles.tableRow"
              >
                <div class="table-cell header">
                  <base-check-box
                    data-test="check-box bulk"
                    :is-checked="bulkChecked"
                    @input="toggleBulkChecked"
                  />
                </div>
                <div class="table-cell header">検査項目</div>
                <div
                  v-for="result in measurementResults"
                  :key="result.id"
                  class="table-cell header category-date"
                >
                  <div>{{ toDate(result) }}</div>
                  <div>{{ toCategoryName(result) }}</div>
                </div>
                <div class="table-cell header">単位</div>
                <div class="table-cell header">基準値</div>
              </div>
              <div
                class="table-body"
                data-test="table-body"
                :style="styles.tableRow"
              >
                <div
                  v-for="(row, rowIndex) in measurementResultItemsToPreview"
                  class="table-body-row"
                  data-test="table-body-row"
                  :key="row.itemCode"
                  :style="styles.tableRow"
                >
                  <div class="table-cell check">
                    <base-check-box
                      data-test="check-box item"
                      :is-checked="row.checked"
                      @input="toggleItemChecked(rowIndex, $event)"
                    />
                  </div>
                  <div
                    class="table-cell name"
                    data-test="table-cell name"
                    :class="{ alert: row.requiredAlert }"
                  >
                    <span>{{ row.itemEnName }}</span>
                  </div>
                  <div
                    v-for="(value, valueIndex) in row.values"
                    class="table-cell value"
                    data-test="table-cell value"
                    :key="valueIndex"
                  >
                    <span>{{ value }}</span>
                  </div>
                  <div class="table-cell unit">{{ row.unit }}</div>
                  <div class="table-cell range">{{ row.range }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </popup>
    </div>
  </focus-trap>
</template>

<script>
import BaseCheckBox from '@/components/parts/atoms/BaseCheckBox'
import Popup from '@/components/popups/Popup'
import dedent from 'dedent'
import moment from 'moment'
import { FocusTrap } from 'focus-trap-vue'
import {
  toDisplayItemName,
  toDisplayRange,
  toDisplayValueInPrint
} from '@/utils/measurement_utils'
import { mapGetters } from 'vuex'
import { printMeasurementResults } from '@/utils/print_measurement_results'

export default {
  name: 'PrintMeasurementResultsPopup',

  components: {
    BaseCheckBox,
    FocusTrap,
    Popup
  },

  props: {
    layerNumber: { type: Number, default: 1 },
    measurementResults: { type: Array, default: () => [] },
    owner: { type: Object, default: () => {} },
    patient: { type: Object, default: () => {} }
  },

  data() {
    return {
      alertDescription: dedent`
        ※下記の検査項目では「単位」と「基準値」が検査結果毎に異なっているため、最新の検査結果の「単位」と「基準値」を表示しています。
      `,
      alertText: '',
      bulkChecked: true,
      buttons: ['閉じる', '印刷'],
      measurementResultItemsToPreview: [] // pdf で再利用する
    }
  },

  computed: {
    ...mapGetters({
      clinic: 'clinic/getData',
      getLaboratoryItem: 'laboratoryItems/getLaboratoryItem',
      getMasterDatum: 'master/getDataById',
      getMasterLaboratoryCategory:
        'master/getMasterLaboratoryCategoryIndexedByCode',
      getMasterLaboratoryItem: 'master/getMasterLaboratoryItemIndexedByCode',
      getMeasurementResultItems:
        'measurementResultItems/getDataByMeasurementResultId',
      getSpecies: 'species/getDataById',
      printSetting: 'printSetting/getData'
    }),
    canPrint() {
      return this.measurementResultItemsToPreview.some(r => r.checked)
    },
    gridColumnCounts() {
      const defaultColumnCounts = 4 // チェック、検査項目、単位、基準値の列
      return defaultColumnCounts + this.measurementResults.length
    },
    styles() {
      const counts = this.gridColumnCounts
      return {
        table: { gridTemplateColumns: `50px repeat(${counts - 1}, auto)` },
        tableRow: { gridColumn: `span ${counts}` }
      }
    }
  },

  created() {
    this.setMeasurementResultItemsToPreview()
    this.setAlert()
  },

  methods: {
    handleClose() {
      this.$emit('close')
    },
    hasSameUnitRange(measurementResultItemToPrint, measurementResultItem) {
      return (
        measurementResultItemToPrint.unit === measurementResultItem.unit &&
        measurementResultItemToPrint.rangeLow ===
          measurementResultItem.referenceRangeLow &&
        measurementResultItemToPrint.rangeHigh ===
          measurementResultItem.referenceRangeHigh
      )
    },
    makePrintData() {
      const checkedItems = this.measurementResultItemsToPreview.filter(
        r => r.checked
      )
      const checkedResultIdSet = new Set(
        checkedItems.flatMap(item =>
          item.measurementResultIds.filter(id => !!id)
        )
      )
      const measurementResultsToPrint = this.measurementResults
        .filter(m => checkedResultIdSet.has(m.id))
        .map(m => {
          return {
            date: m.date,
            categoryName: this.toCategoryName(m)
          }
        })
      const indexShowValues = this.measurementResults.map(result =>
        checkedResultIdSet.has(result.id)
      )
      const measurementResultItemsToPrint = checkedItems.map(item => {
        const values = item.values.filter((_, i) => indexShowValues[i])
        return { ...item, values }
      })
      return {
        measurementResultsToPrint,
        measurementResultItemsToPrint
      }
    },
    openPdf() {
      const species = this.getSpecies(this.patient.speciesId)
      const speciesName = species?.name ?? ''
      const prefectureId = this.clinic.prefectureId
      const prefecture = this.getMasterDatum('prefectures', prefectureId)
      const prefectureName = prefecture?.name ?? ''
      const clinic = { ...this.clinic, prefectureName }
      printMeasurementResults({
        clinic,
        clinicImage: this.printSetting.image,
        showClinicName: Boolean(this.printSetting.printClinicNameFlg),
        owner: this.owner,
        patient: this.patient,
        speciesName,
        printData: this.makePrintData()
      })
    },
    setMeasurementResultItemsToPreview() {
      const resultCounts = this.measurementResults.length
      const rowsMap = this.measurementResults.reduce(
        (map, result, resultIndex) => {
          const items = this.getMeasurementResultItems(result.id) ?? []
          items.forEach(item => {
            const itemCode = item.masterLaboratoryItemCode
            const masterItem = this.getMasterLaboratoryItem(itemCode)
            const itemEnName = toDisplayItemName(masterItem?.enName, item)
            const itemJaName = toDisplayItemName(masterItem?.jaName, item)
            const existing = map.get(itemCode)
            if (existing) {
              existing.requiredAlert = !this.hasSameUnitRange(existing, item)
              existing.measurementResultIds[resultIndex] = result.id
              existing.itemEnName = itemEnName
              existing.itemJaName = itemJaName
              existing.values[resultIndex] = toDisplayValueInPrint(
                result.masterLaboratoryInstrumentCode,
                item,
                existing.rangeLow,
                existing.rangeHigh
              )
            } else {
              const measurementResultIds = Array(resultCounts).fill(0)
              measurementResultIds[resultIndex] = result.id
              const values = Array(resultCounts).fill('')
              values[resultIndex] = toDisplayValueInPrint(
                result.masterLaboratoryInstrumentCode,
                item,
                item.referenceRangeLow,
                item.referenceRangeHigh
              )
              const laboratoryItem = this.getLaboratoryItem(
                result.masterLaboratoryInstrumentCode,
                itemCode
              )
              const description = laboratoryItem?.description ?? ''
              map.set(itemCode, {
                checked: true,
                requiredAlert: false,
                measurementResultIds,
                itemCode,
                itemEnName,
                itemJaName, // pdf で使用
                values, // pdf で再使用
                unit: item.unit, // pdf で再使用
                rangeLow: item.referenceRangeLow,
                rangeHigh: item.referenceRangeHigh,
                range: this.toRange(item), // pdf で再使用
                description // pdf で使用
              })
            }
          })
          return map
        },
        new Map()
      )
      this.measurementResultItemsToPreview = Array.from(rowsMap.values())
    },
    setAlert() {
      // ※ measurementResultItemsToPreview 設定済み
      this.alertText = this.measurementResultItemsToPreview
        .filter(r => r.requiredAlert)
        .map(r => `・${r.itemEnName}`)
        .join('\n')
    },
    toggleBulkChecked(checked) {
      this.bulkChecked = checked
      this.measurementResultItemsToPreview = this.measurementResultItemsToPreview.map(
        r => ({
          ...r,
          checked
        })
      )
    },
    toggleItemChecked(index, checked) {
      this.measurementResultItemsToPreview[index].checked = checked
      if (!checked) {
        this.bulkChecked = false
      }
    },
    toCategoryName(measurementResult) {
      const category = this.getMasterLaboratoryCategory(
        measurementResult.masterLaboratoryCategoryCode
      )
      return category?.name ?? ''
    },
    toDate(measurementResult) {
      return moment(measurementResult.date, 'YYYYMMDD').format('YYYY/MM/DD')
    },
    toRange(measurementResultItem) {
      const { referenceRangeLow, referenceRangeHigh } = measurementResultItem
      return toDisplayRange(referenceRangeLow, referenceRangeHigh)
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin table-cell {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-right: 1px solid #{$light-grey};
  border-bottom: 1px solid #{$light-grey};
  font-size: 13px;
  overflow-wrap: anywhere;
  line-break: strict;
}

.print-measurement-results-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    width: 650px;
    position: relative;
    > .title {
      position: sticky;
      top: 0;
      z-index: calc(100 * 1);
      height: 43px;
      text-align: left;
      font-size: 16px;
      font-weight: bold;
      color: #{$greyish-brown};
      background-color: #{$white};
    }
    > .alert {
      margin-bottom: 20px;
      text-align: left;
      font-size: 15px;
      > .text {
        margin-top: 6px;
        color: #{$tomato};
        font-size: 14px;
        white-space: pre-wrap;
      }
    }
    > .table {
      box-sizing: border-box;
      display: grid;
      // ※下記の項目は動的に変更するため、style 属性で定義
      // grid-template-columns
      > .table-header {
        position: sticky;
        top: 43px;
        z-index: calc(100 * 1);
        display: grid;
        grid-template-columns: subgrid;
        border-top: 1px solid #{$light-grey};
        border-left: 1px solid #{$light-grey};
        // ※下記の項目は動的に変更するため、style 属性で定義
        // grid-column
        > .table-cell {
          @include table-cell;
          &.header {
            background-color: #{$very-light-shade-orange};
            &.category-date {
              flex-direction: column;
            }
          }
        }
      }
      > .table-body {
        box-sizing: border-box;
        display: grid;
        grid-template-columns: subgrid;
        border-left: 1px solid #{$light-grey};
        // ※下記の項目は動的に変更するため、style 属性で定義
        // grid-column
        > .table-body-row {
          display: grid;
          grid-template-columns: subgrid;
          // ※下記の項目は動的に変更するため、style 属性で定義
          // grid-column
          &:nth-child(even) {
            background-color: #f7f7f7;
          }
          > .table-cell {
            @include table-cell;
            &.name {
              justify-content: flex-start;
              &.alert {
                color: #{$tomato};
              }
            }
          }
        }
      }
    }
  }
}
</style>
